import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast, Toaster } from 'sonner';
import axios from 'axios';
import {
    Plus, Search, Briefcase, 
    User, Phone, Mail, MapPin, 
    X, Edit3, Trash2, Loader2,
    Building2, ClipboardList
} from 'lucide-react';

const API_BASE = (import.meta.env.VITE_APP_API_URL && import.meta.env.VITE_APP_API_URL !== "undefined") ? import.meta.env.VITE_APP_API_URL : "https://apiessentialcarehomehealth.saasbizhub.com/api/v1";

export default function CrmReferralSources() {
    const [sources, setSources] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [selectedSource, setSelectedSource] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const fetchSources = async () => {
        const token = localStorage.getItem('admin_token');
        try {
            setLoading(true);
            const response = await axios.get(`${API_BASE}/admin/crm/referral-sources`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSources(response.data);
        } catch (err) {
            toast.error('Failed to load referral sources');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSources();
    }, []);

    const handleCreateSource = async (e) => {
        e.preventDefault();
        const formData = new FormData(e.target);
        const data = Object.fromEntries(formData.entries());
        const token = localStorage.getItem('admin_token');

        try {
            setIsSubmitting(true);
            await axios.post(`${API_BASE}/admin/crm/referral-sources`, data, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Referral source added successfully');
            setIsCreateModalOpen(false);
            fetchSources();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to add source');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleUpdateSource = async (e) => {
        e.preventDefault();
        if (!selectedSource) return;
        const formData = new FormData(e.target);
        const data = Object.fromEntries(formData.entries());
        const token = localStorage.getItem('admin_token');

        try {
            setIsSubmitting(true);
            await axios.put(`${API_BASE}/admin/crm/referral-sources/${selectedSource.id}`, data, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Referral source updated successfully');
            setIsEditModalOpen(false);
            fetchSources();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to update source');
        } finally {
            setIsSubmitting(false);
        }
    };

    const filteredSources = sources.filter(s => 
        s.facility_name.toLowerCase().includes(search.toLowerCase()) ||
        s.contact_person.toLowerCase().includes(search.toLowerCase())
    );

    return (
        <div className="p-8 max-w-7xl mx-auto space-y-8">
            <Toaster position="top-right" />
            
            <header className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Referral Sources</h1>
                    <p className="text-gray-500 mt-1">Manage partner facilities and contacts.</p>
                </div>
                <button 
                    onClick={() => setIsCreateModalOpen(true)}
                    className="inline-flex items-center px-5 py-2.5 bg-teal-600 hover:bg-teal-700 text-white font-medium rounded-xl shadow-lg transition-all active:scale-95"
                >
                    <Plus className="w-5 h-5 mr-2" />
                    Add New Source
                </button>
            </header>

            <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100">
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input 
                        type="text" 
                        placeholder="Search by facility name or contact..."
                        className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-teal-500 transition-all"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {loading ? (
                    Array(6).fill(0).map((_, i) => (
                        <div key={i} className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 animate-pulse h-48" />
                    ))
                ) : filteredSources.length > 0 ? (
                    filteredSources.map((source) => (
                        <motion.div 
                            layout
                            key={source.id}
                            className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow relative group"
                        >
                            <div className="flex justify-between items-start mb-4">
                                <div className="p-3 bg-teal-50 rounded-xl text-teal-600">
                                    <Building2 className="w-6 h-6" />
                                </div>
                                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <button 
                                        onClick={() => { setSelectedSource(source); setIsEditModalOpen(true); }}
                                        className="p-2 text-gray-400 hover:text-teal-600 rounded-lg hover:bg-teal-50"
                                    >
                                        <Edit3 className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                            <h3 className="font-bold text-gray-900 text-lg mb-1">{source.facility_name}</h3>
                            <div className="space-y-2 text-sm text-gray-600">
                                <p className="flex items-center gap-2">
                                    <User className="w-4 h-4 text-gray-400" /> {source.contact_person}
                                </p>
                                <p className="flex items-center gap-2">
                                    <Phone className="w-4 h-4 text-gray-400" /> {source.phone}
                                </p>
                                <p className="flex items-center gap-2">
                                    <Mail className="w-4 h-4 text-gray-400" /> {source.email}
                                </p>
                            </div>
                        </motion.div>
                    ))
                ) : (
                    <div className="col-span-full py-12 text-center text-gray-500 bg-white rounded-2xl border border-dashed border-gray-200">
                        <ClipboardList className="w-12 h-12 text-gray-200 mx-auto mb-2" />
                        <p>No referral sources found.</p>
                    </div>
                )}
            </div>

            {/* Create/Edit Modals would go here (similar to CrmLeads) */}
            <AnimatePresence>
                {(isCreateModalOpen || isEditModalOpen) && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => { setIsCreateModalOpen(false); setIsEditModalOpen(false); }} className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm" />
                        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden relative" >
                            <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                                <h2 className="text-xl font-bold text-gray-900">{isEditModalOpen ? 'Edit Source' : 'Add New Source'}</h2>
                                <button onClick={() => { setIsCreateModalOpen(false); setIsEditModalOpen(false); }} className="text-gray-400 hover:text-gray-600"><X className="w-6 h-6" /></button>
                            </div>
                            <form onSubmit={isEditModalOpen ? handleUpdateSource : handleCreateSource} className="p-6 space-y-4">
                                <div className="space-y-2">
                                    <label className="text-sm font-semibold text-gray-700">Facility Name *</label>
                                    <input name="facility_name" defaultValue={selectedSource?.facility_name} required className="w-full px-4 py-2 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-teal-500" />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-sm font-semibold text-gray-700">Contact Person *</label>
                                    <input name="contact_person" defaultValue={selectedSource?.contact_person} required className="w-full px-4 py-2 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-teal-500" />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <label className="text-sm font-semibold text-gray-700">Phone *</label>
                                        <input name="phone" defaultValue={selectedSource?.phone} required className="w-full px-4 py-2 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-teal-500" />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-sm font-semibold text-gray-700">Email *</label>
                                        <input name="email" type="email" defaultValue={selectedSource?.email} required className="w-full px-4 py-2 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-teal-500" />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-sm font-semibold text-gray-700">Address *</label>
                                    <input name="address" defaultValue={selectedSource?.address} required className="w-full px-4 py-2 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-teal-500" />
                                </div>
                                <div className="pt-4 flex justify-end gap-3">
                                    <button type="button" onClick={() => { setIsCreateModalOpen(false); setIsEditModalOpen(false); }} className="px-6 py-2 text-gray-600 font-medium hover:bg-gray-100 rounded-xl">Cancel</button>
                                    <button type="submit" disabled={isSubmitting} className="px-6 py-2 bg-teal-600 text-white font-medium rounded-xl shadow-lg flex items-center gap-2">
                                        {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : (isEditModalOpen ? 'Update' : 'Add Source')}
                                    </button>
                                </div>
                            </form>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
}
